import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import Dialog from '@mui/material/Dialog';
import QRCode from 'react-qr-code';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import html2canvas from 'html2canvas';

const Tablelistview = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rows, setRows] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [qrPopupOpen, setQrPopupOpen] = useState(false);
  const [qrCodeUrl, setQRCodeUrl] = useState('');
  const [tableNo, setTableNo] = useState('');
  const apiUrl = process.env.REACT_APP_API_URL;

  // ✅ FIXED: useCallback to prevent infinite loop
  const fetchTables = useCallback(async () => {
    try {
      const storedToken = localStorage.getItem('accessToken');
      const response = await fetch(`${apiUrl}/tables`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storedToken}`,
        },
      });
      const data = await response.json();
      setRows(data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [apiUrl]);

  // ✅ FIXED: proper dependency
  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClick = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const openQrPopup = () => {
    const currentRowId = selectedRowId;
    setQrPopupOpen(true);
    handleClose();

    const storedToken = localStorage.getItem('accessToken');

    fetch(`${apiUrl}/tables/qrurl/${currentRowId}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${storedToken}`,
      },
    })
      .then((response) => response.json())
      .then((qrCodeData) => {
        setQRCodeUrl(qrCodeData.qrCodeUrl);
        setTableNo(qrCodeData.tableNo);
      })
      .catch((error) => console.error('Error fetching QR code data:', error));
  };

  const closeQrPopup = () => {
    setQrPopupOpen(false);
    setQRCodeUrl('');
  };

  const downloadQRCode = async () => {
    try {
      const dialogContent = document.querySelector('.popupContent');
      const canvas = await html2canvas(dialogContent, {
        ignoreElements: (element) =>
          element.classList.contains('popupActions') ||
          element.classList.contains('tableInfo'),
      });

      const imageDataURL = canvas.toDataURL('image/png');

      const link = document.createElement('a');
      link.href = imageDataURL;
      link.download = `qrcode_table_${tableNo}_screenshot.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error generating QR code screenshot:', error);
    }
  };

  const handleDelete = async () => {
    if (!selectedRowId) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete table "${tableNo || selectedRowId}"?`
    );

    if (!confirmDelete) {
      handleClose();
      return;
    }

    try {
      const storedToken = localStorage.getItem('accessToken');
      const response = await fetch(`${apiUrl}/tables/${selectedRowId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storedToken}`,
        },
      });

      if (response.ok) {
        await fetchTables();
        console.log('Table deleted successfully');
      } else {
        const errorData = await response.json();
        alert(`Delete failed: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      alert('Error deleting table');
    } finally {
      handleClose();
    }
  };

  return (
    <div>
      <div className="navbar">
        <div className="wrapper">
          <div className="search">
            <input type="text" placeholder="Search..." />
            <SearchOutlinedIcon className="icon" />
          </div>
          <div className="items">
            <DownloadIcon className="icon" />
          </div>
        </div>
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Table No</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => (
                <TableRow key={row._id}>
                  <TableCell>{row._id}</TableCell>
                  <TableCell>{row.tableno}</TableCell>
                  <TableCell>{new Date(row.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(row.updatedAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <MoreVertIcon onClick={(e) => handleClick(e, row._id)} />

                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl && selectedRowId === row._id)}
                      onClose={handleClose}
                    >
                      <MenuItem onClick={openQrPopup}>QR</MenuItem>
                      <MenuItem component={Link} to={`/tables/update/${row._id}`}>
                        Update
                      </MenuItem>
                      <MenuItem onClick={handleDelete}>
                        <DeleteIcon fontSize="small" /> Delete
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={qrPopupOpen} onClose={closeQrPopup}>
        <div className="popupContent">
          <p>Table No: {tableNo}</p>
          {qrCodeUrl && <QRCode value={qrCodeUrl} />}
          <IconButton onClick={closeQrPopup}>
            <CloseIcon />
          </IconButton>
          <IconButton onClick={downloadQRCode}>
            <DownloadIcon />
          </IconButton>
        </div>
      </Dialog>
    </div>
  );
};

export default Tablelistview;